import { formatDateDisplay, formatDateTimeInBusinessTZ } from '../utils/date';
import type { BhcsStudentWaiver } from '../types/waiver';
import { DEFAULT_BHCS_WAIVER_SIGNATURE_FONT_STACK } from '../types/waiver';

interface PrintableWaiverProps {
  studentName: string;
  waiver: BhcsStudentWaiver;
}

function renderValue(value?: string): string {
  return value && value.trim() ? value : '—';
}

export function PrintableWaiver({ studentName, waiver }: PrintableWaiverProps) {
  const { parent1, parent2, emergencyContact, hasAllergies, allergiesDetails, physician, insurance, consents, signature, submittedAt } = waiver;

  return (
    <div className="printable-waiver relative z-20 max-w-4xl mx-auto bg-white p-8 print:p-4">
      {/* Header */}
      <div className="flex justify-between items-start mb-8 print:mb-6">
        <div className="flex flex-col">
          <div className="mb-2">
            <h2 className="text-xl font-semibold print:text-lg">Bright Minds Collective LLC</h2>
            <p className="text-gray-600 text-sm">Student Waiver · Bright Horizon Chinese School and Kaleido</p>
          </div>
          <p className="text-gray-600">650 Shell Blvd</p>
          <p className="text-gray-600">Foster City, CA 94404</p>
          <p className="text-gray-600">Phone: 650-620-0299</p>
          <p className="text-gray-600">Email: billing@bhcskids.com</p>
        </div>

        <div className="text-right">
          <h1 className="text-2xl font-bold text-gray-900 print:text-xl">WAIVER</h1>
          <p className="text-gray-600">{formatDateTimeInBusinessTZ(submittedAt)}</p>
        </div>
      </div>

      {/* Student */}
      <div className="mb-8 print:mb-6">
        <h3 className="font-semibold text-gray-900 mb-2">Student</h3>
        <div className="text-gray-800">
          <div><span className="text-gray-600">Name:</span> {studentName}</div>
        </div>
      </div>

      {/* Contacts */}
      <div className="mb-8 print:mb-6">
        <h3 className="font-semibold text-gray-900 mb-3">Contacts</h3>

        <div className="grid grid-cols-1 grid-cols-2 gap-6">
          <div className="border border-gray-200 rounded-md p-4">
            <div className="font-semibold text-gray-900 mb-2">Parent/Guardian 1</div>
            <div className="text-sm text-gray-800 space-y-1">
              <div><span className="text-gray-600">Name:</span> {renderValue(parent1.name)}</div>
              <div><span className="text-gray-600">Relationship:</span> {renderValue(parent1.relationship)}</div>
              <div><span className="text-gray-600">Phone:</span> {renderValue(parent1.phone)}</div>
              <div><span className="text-gray-600">Email:</span> {renderValue(parent1.email)}</div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-md p-4">
            <div className="font-semibold text-gray-900 mb-2">Parent/Guardian 2 (optional)</div>
            <div className="text-sm text-gray-800 space-y-1">
              <div><span className="text-gray-600">Name:</span> {renderValue(parent2?.name)}</div>
              <div><span className="text-gray-600">Relationship:</span> {renderValue(parent2?.relationship)}</div>
              <div><span className="text-gray-600">Phone:</span> {renderValue(parent2?.phone)}</div>
              <div><span className="text-gray-600">Email:</span> {renderValue(parent2?.email)}</div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-md p-4 col-span-2">
            <div className="font-semibold text-gray-900 mb-2">Emergency Contact</div>
            <div className="text-sm text-gray-800 space-y-1">
              <div><span className="text-gray-600">Name:</span> {renderValue(emergencyContact.name)}</div>
              <div><span className="text-gray-600">Relationship:</span> {renderValue(emergencyContact.relationship)}</div>
              <div><span className="text-gray-600">Phone:</span> {renderValue(emergencyContact.phone)}</div>
              <div><span className="text-gray-600">Email:</span> {renderValue(emergencyContact.email)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Health */}
      <div className="mb-8 print:mb-6">
        <h3 className="font-semibold text-gray-900 mb-3">Health & Insurance</h3>
        <div className="grid grid-cols-1 grid-cols-2 gap-6">
          <div className="border border-gray-200 rounded-md p-4">
            <div className="font-semibold text-gray-900 mb-2">Allergies</div>
            <div className="text-sm text-gray-800 space-y-1">
              <div>
                <span className="text-gray-600">Has allergies:</span> {hasAllergies ? 'Yes' : 'No'}
              </div>
              {hasAllergies && (
                <div>
                  <span className="text-gray-600">Details:</span> {renderValue(allergiesDetails)}
                </div>
              )}
            </div>
          </div>

          <div className="border border-gray-200 rounded-md p-4">
            <div className="font-semibold text-gray-900 mb-2">Physician (optional)</div>
            <div className="text-sm text-gray-800 space-y-1">
              <div><span className="text-gray-600">Name:</span> {renderValue(physician?.name)}</div>
              <div><span className="text-gray-600">Phone:</span> {renderValue(physician?.phone)}</div>
              <div><span className="text-gray-600">Address:</span> {renderValue(physician?.address)}</div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-md p-4 col-span-2">
            <div className="font-semibold text-gray-900 mb-2">Insurance (optional)</div>
            <div className="text-sm text-gray-800 grid grid-cols-1 grid-cols-3 gap-3">
              <div><span className="text-gray-600">Provider:</span> {renderValue(insurance?.provider)}</div>
              <div><span className="text-gray-600">Policy #:</span> {renderValue(insurance?.policyNumber)}</div>
              <div><span className="text-gray-600">Group #:</span> {renderValue(insurance?.groupNumber)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Consents */}
      <div className="mb-8 print:mb-6">
        <h3 className="font-semibold text-gray-900 mb-3">Consents</h3>
        <ul className="text-sm text-gray-800 space-y-2">
          <li>
            <span className="font-medium">Emergency medical authorization:</span> {consents.emergencyMedicalAuthorization ? 'Accepted' : 'Not accepted'}
          </li>
          <li>
            <span className="font-medium">Liability waiver:</span> {consents.liabilityWaiver ? 'Accepted' : 'Not accepted'}
          </li>
          <li>
            <span className="font-medium">Assumption of risk:</span> {consents.assumptionOfRisk ? 'Accepted' : 'Not accepted'}
          </li>
          <li>
            <span className="font-medium">Behavior policy:</span> {consents.behaviorPolicy ? 'Accepted' : 'Not accepted'}
          </li>
          <li>
            <span className="font-medium">Infectious disease disclaimer:</span> {consents.infectiousDiseaseDisclaimer ? 'Accepted' : 'Not accepted'}
          </li>
          <li>
            <span className="font-medium">Photo release:</span> {consents.photoRelease ? 'Permission granted' : 'Permission NOT granted'}
          </li>
        </ul>
      </div>

      {/* Signature */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="font-semibold text-gray-900 mb-3">Signature</h3>
        <div className="grid grid-cols-1 grid-cols-2 gap-6">
          <div className="text-sm text-gray-800 space-y-1">
            <div><span className="text-gray-600">Printed name:</span> {renderValue(signature.printedName)}</div>
            <div>
              <span className="text-gray-600">Signature:</span>{' '}
              <span
                className="font-medium text-lg"
                style={{ fontFamily: signature.fontStack || DEFAULT_BHCS_WAIVER_SIGNATURE_FONT_STACK }}
              >
                {renderValue(signature.signature)}
              </span>
            </div>
            <div><span className="text-gray-600">Date:</span> {formatDateDisplay(signature.date)}</div>
          </div>
          <div className="text-sm text-gray-800 space-y-1 sm:text-right">
            <div><span className="text-gray-600">Submitted:</span> {formatDateTimeInBusinessTZ(submittedAt)}</div>
            <div className="text-gray-500 text-xs">All dates/times shown in America/Los_Angeles</div>
          </div>
        </div>
      </div>
    </div>
  );
}


